import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Building2, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormDrawer } from "@/components/ui/form-drawer";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { DateCell } from "@/components/DateCell";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface Lead {
  id: string;
  name: string;
  code: string | null;
  classification: string;
  status: string;
  creditLimit: string | null;
  creditHold: boolean;
  createdAt: string;
  updatedAt: string | null;
}

interface LeadsResponse {
  leads: Lead[];
  total: number;
  page: number;
  limit: number;
}

const leadFormSchema = z.object({
  name: z.string().min(1, "Company name is required"),
  locationName: z.string().min(1, "Location name is required"),
  email: z.string().email("Please enter a valid email").optional().or(z.literal("")),
  phone: z.string().optional(),
  industry: z.string().optional(),
  website: z.string().optional(),
  leadSource: z.string().optional(),
  notes: z.string().optional(),
});

type LeadFormData = z.infer<typeof leadFormSchema>;

const leadSources = [
  { value: "website", label: "Website" },
  { value: "referral", label: "Referral" },
  { value: "trade_show", label: "Trade Show" },
  { value: "cold_call", label: "Cold Call" },
  { value: "social_media", label: "Social Media" },
  { value: "advertisement", label: "Advertisement" },
  { value: "other", label: "Other" },
];

const industries = [
  "Food Service",
  "Retail",
  "Healthcare",
  "Education",
  "Hospitality",
  "Manufacturing",
  "Wholesale",
  "Other",
];

export default function LeadsPage() {
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState("");
  const [newLeadDrawerOpen, setNewLeadDrawerOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<LeadFormData>({
    resolver: zodResolver(leadFormSchema),
    defaultValues: {
      name: "",
      locationName: "Main",
      email: "",
      phone: "",
      industry: "",
      website: "",
      leadSource: "",
      notes: "",
    },
  });

  const { data, isLoading } = useQuery<LeadsResponse>({
    queryKey: ["/api/leads", { page: currentPage, limit: pageSize, search: searchQuery }],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        ...(searchQuery && { search: searchQuery }),
      });
      return api.get<LeadsResponse>(`/leads?${params}`, sessionId);
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: LeadFormData) => {
      return api.post<Lead>("/leads", data, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      toast({
        title: "Lead created",
        description: "The lead has been created successfully.",
      });
      setNewLeadDrawerOpen(false);
      form.reset();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create lead",
        variant: "destructive",
      });
    },
  });

  const convertMutation = useMutation({
    mutationFn: async (leadId: string) => {
      return api.post<Lead>(`/leads/${leadId}/convert`, {}, sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
      toast({
        title: "Lead converted",
        description: "The lead has been successfully converted to an account.",
      });
      setConvertDialogOpen(false);
      setSelectedLead(null);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to convert lead.",
        variant: "destructive",
      });
    },
  });

  const handleOpenNewLead = () => {
    form.reset();
    setNewLeadDrawerOpen(true);
  };

  const handleDrawerClose = (open: boolean) => {
    setNewLeadDrawerOpen(open);
    if (!open) {
      form.reset();
    }
  };

  const handleSubmit = (data: LeadFormData) => {
    createMutation.mutate(data);
  };

  const handleConvert = (lead: Lead) => {
    setSelectedLead(lead);
    setConvertDialogOpen(true);
  };

  const handleConfirmConvert = () => {
    if (selectedLead) {
      convertMutation.mutate(selectedLead.id);
    }
  };

  const columns = [
    {
      key: "name",
      header: "Company",
      sortable: true,
      cell: (lead: Lead) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <button
            onClick={() => setLocation(`/leads/${lead.id}`)}
            className="font-medium hover:underline text-left"
          >
            {lead.name}
          </button>
        </div>
      ),
    },
    {
      key: "code",
      header: "Code",
      cell: (lead: Lead) => (
        <span className="text-muted-foreground">{lead.code || "—"}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (lead: Lead) => <StatusBadge status={lead.status} type="account" />,
    },
    {
      key: "createdAt",
      header: "Created",
      cell: (lead: Lead) => <DateCell value={lead.createdAt} />,
    },
    {
      key: "actions",
      header: "",
      cell: (lead: Lead) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleConvert(lead)}
          >
            <ArrowRight className="h-4 w-4 mr-1" />
            Convert
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Leads"
        description="Manage your sales leads and prospects"
        actions={
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search leads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64"
            />
            <Button onClick={handleOpenNewLead} data-testid="button-new-lead">
              <Plus className="h-4 w-4 mr-2" />
              New Lead
            </Button>
          </div>
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={data?.leads || []}
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={data?.total || 0}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {/* New Lead Drawer */}
      <FormDrawer
        open={newLeadDrawerOpen}
        onOpenChange={handleDrawerClose}
        title="New Lead"
        description="Enter the lead's information to add them to your pipeline."
        formId="create-lead"
        isPending={createMutation.isPending}
        submitLabel="Create Lead"
        size="md"
        data-testid="drawer-new-lead"
      >
        <Form {...form}>
          <form
            id="create-lead"
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Name *</FormLabel>
                  <FormControl>
                    <Input
                      variant="flat"
                      placeholder="Enter company name"
                      data-testid="input-lead-name"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="locationName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location Name *</FormLabel>
                  <FormControl>
                    <Input
                      variant="flat"
                      placeholder="e.g. Main, HQ, Warehouse"
                      data-testid="input-location-name"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="industry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Industry</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || undefined}>
                      <FormControl>
                        <SelectTrigger variant="flat" data-testid="select-lead-industry">
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {industries.map((industry) => (
                          <SelectItem key={industry} value={industry.toLowerCase()}>
                            {industry}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="leadSource"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lead Source</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || undefined}>
                      <FormControl>
                        <SelectTrigger variant="flat" data-testid="select-lead-source">
                          <SelectValue placeholder="Select source" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {leadSources.map((source) => (
                          <SelectItem key={source.value} value={source.value}>
                            {source.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Website</FormLabel>
                  <FormControl>
                    <Input
                      variant="flat"
                      placeholder="https://example.com"
                      data-testid="input-lead-website"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      variant="flat"
                      placeholder="Any additional notes about this lead..."
                      className="min-h-[100px]"
                      data-testid="input-lead-notes"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </FormDrawer>

      {/* Convert Lead Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert Lead to Account</DialogTitle>
            <DialogDescription>
              Convert "{selectedLead?.name}" from a lead to a customer account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This will change the classification from "prospect" to "customer" and make
              this lead a full account in your system.
            </p>
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setConvertDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmConvert}
                disabled={convertMutation.isPending}
              >
                {convertMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Convert to Account
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
